import { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

interface GeolocationState {
  lat: number | null;
  lng: number | null;
  speed: number | null;
  heading: number | null;
  accuracy: number | null;
  timestamp: number | null;
  error: string | null;
  isNative: boolean;
}

const isNativePlatform = Capacitor.isNativePlatform();

export function useGeolocation(enabled: boolean = false) {
  const [state, setState] = useState<GeolocationState>({
    lat: null,
    lng: null,
    speed: null,
    heading: null,
    accuracy: null,
    timestamp: null,
    error: null,
    isNative: isNativePlatform,
  });
  const watchIdRef = useRef<string | number | null>(null);

  const startNative = useCallback(async () => {
    try {
      // Request permission on native
      const perm = await Geolocation.requestPermissions();
      if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
        setState((s) => ({ ...s, error: 'Location permission denied' }));
        return;
      }

      // Capacitor watchPosition — works in background on native
      const id = await Geolocation.watchPosition(
        { enableHighAccuracy: true, maximumAge: 3000, timeout: 10000 },
        (pos, err) => {
          if (err) {
            setState((s) => ({ ...s, error: err.message }));
            return;
          }
          if (pos) {
            setState({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              speed: pos.coords.speed,
              heading: pos.coords.heading,
              accuracy: pos.coords.accuracy,
              timestamp: pos.timestamp,
              error: null,
              isNative: true,
            });
          }
        }
      );
      watchIdRef.current = id;
    } catch (err) {
      setState((s) => ({
        ...s,
        error: err instanceof Error ? err.message : 'Native GPS failed',
      }));
    }
  }, []);

  const startBrowser = useCallback(() => {
    if (!navigator.geolocation) {
      setState((s) => ({ ...s, error: 'Geolocation not supported' }));
      return;
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setState({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          speed: pos.coords.speed,
          heading: pos.coords.heading,
          accuracy: pos.coords.accuracy,
          timestamp: pos.timestamp,
          error: null,
          isNative: false,
        });
      },
      (err) => {
        setState((s) => ({ ...s, error: err.message }));
      },
      {
        enableHighAccuracy: true,
        maximumAge: 3000,
        timeout: 10000,
      }
    );
  }, []);

  const start = useCallback(() => {
    if (isNativePlatform) {
      startNative();
    } else {
      startBrowser();
    }
  }, [startNative, startBrowser]);

  const stop = useCallback(async () => {
    if (watchIdRef.current !== null) {
      if (isNativePlatform) {
        await Geolocation.clearWatch({ id: watchIdRef.current as string });
      } else {
        navigator.geolocation.clearWatch(watchIdRef.current as number);
      }
      watchIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      start();
    } else {
      stop();
    }
    return () => { stop(); };
  }, [enabled, start, stop]);

  return { ...state, start, stop };
}
